/**
 * Loading-screen tidbits shown to existing customers while pages load.
 * Goal: educate and build trust by surfacing the depth of work behind the app.
 *
 * Each entry: { text, platforms, products? }
 *   text: may contain the placeholder {app}, which resolves to the current
 *         brand name (Truple / Best Accountability / ScreenKin). Tidbits
 *         that reference brand history (e.g., "launched in 2017") intentionally
 *         hardcode "Truple".
 *   platforms: subset of ['android','kindle','ios','mac','windows','linux','chromebook']
 *              or ['all'] when the tip is universal.
 *   products: optional subset of ['truple','bestaccountability','screenkin'].
 *             If omitted, the tip is shown for every product.
 *
 * To consume in a component, prefer getLoadingTips() — it returns the array
 * with {app} already resolved and tips filtered to the current product.
 */

function getAppName() {
  const product = getCurrentProduct()
  if (product === 'bestaccountability') return 'Best Accountability'
  if (product === 'screenkin') return 'ScreenKin'
  return 'Truple'
}

function getCurrentProduct() {
  return process.env.VUE_APP_PRODUCT || 'truple'
}

export const LoadingTips = [
  // ---------- Company & product story ----------
  {
    text: 'Truple invented screenshot accountability back in 2017 — we were the first to put this approach in your hands.',
    platforms: ['all'],
  },
  {
    text: "We're a small team — roughly 1/100 the size of our competitors — yet they regularly copy the features we ship first, just years delayed.",
    platforms: ['all'],
  },
  {
    text: '{app} is built by a small team a single pizza can feed.',
    platforms: ['all'],
  },
  {
    text: 'We offer screenshot accountability on 7 platforms: iOS, Android, Windows, Mac, Linux, Chromebook, and Kindle.',
    platforms: ['all'],
  },
  {
    text: 'When reviewing events, consider the timestamp.  Many events can be recorded within less than a second.',
    platforms: ['all']
  },
  {
    text: 'Truple is evolving into two focused apps: ScreenKin for parental controls and Best Accountability for porn accountability. Your single account works with both.',
    platforms: ['all'],
    products: ['truple']
  },
  {
    text: "Read our reviews--tons of highly technical users choose {app} because it's genuinely the most tamper-resistant accountability app available.",
    platforms: ['all'],
    products: ['truple']
  },

  // ---------- Privacy & end-to-end encryption ----------
  {
    text: '{app} is the only accountability app that offers end-to-end encryption.',
    platforms: ['all'],
    products: ['truple', 'bestaccountability']
  },
  {
    text: "With end-to-end encryption, not even {app}'s team can read your data — only you and the people you share access with hold the keys.",
    platforms: ['all'],
  },
  {
    text: "Screenshot analysis happens directly on your device — your images aren't sent off-device for AI scanning.",
    platforms: ['all'],
  },
  {
    text: 'Optional text redaction lets you preserve the privacy of those being monitored — on your terms.',
    platforms: ['all'],
  },
  {
    text: "You're in control of your reports — choose exactly what to share with your accountability partners and what to keep private.",
    platforms: ['all'],
    products: ['truple', 'bestaccountability']
  },

  // ---------- How the AI & screenshots work ----------
  {
    text: "Our AI is trained to notice fine details — small images or content tucked inside a busy screen don't slip past it.",
    platforms: ['all'],
  },
  {
    text: "Screenshots are captured at random intervals — there's no guessing when one is captured.",
    platforms: ['all'],
  },
  {
    text: "You can tune the screenshot scanning algorithm to be more or less sensitive.",
    platforms: ['all'],
  },
  {
    text: '{app} keeps monitoring even offline or in airplane mode. The moment the device reconnects, your reports catch up automatically.',
    platforms: ['all'],
  },
  {
    text: "If someone changes the device's date or time to try to slip past monitoring, {app} notices and triggers a tamper alert.",
    platforms: ['all'],
  },
  {
    text: '{app} tracks websites visited across all popular browsers on most platforms.',
    platforms: ['android', 'windows', 'mac', 'linux', 'chromebook'],
  },

  // ---------- Cross-platform tamper detection ----------
  {
    text: 'On Android, Windows, and macOS, {app} knows when safe mode has been used — and triggers a tamper alert.',
    platforms: ['android', 'windows', 'mac'],
  },

  // ---------- Android / Kindle ----------
  {
    text: "On Android, force-stopping the monitoring service doesn't go unnoticed — {app} triggers a tamper alert.",
    platforms: ['android', 'kindle'],
  },
  {
    text: 'On Android, {app} can detect a factory reset attempt and trigger a tamper alert before it happens.',
    platforms: ['android', 'kindle'],
  },
  {
    text: 'On Samsung devices, entering maintenance or repair mode triggers a tamper alert.',
    platforms: ['android'],
  },
  {
    text: 'On Android, {app} reports if your Private DNS filter has been disabled.',
    platforms: ['android'],
  },
  {
    text: 'On Android, {app} reports if hotspot mode is used while a Private DNS filter is active — a common route around DNS-based filtering.',
    platforms: ['android'],
  },
  {
    text: 'On Android, using an unmonitored secondary display triggers a tamper alert.',
    platforms: ['android'],
  },
  {
    text: 'On Android, {app} alerts when incognito or private browsing mode is detected.',
    platforms: ['android'],
  },
  {
    text: "On Android, switching to a different user account triggers a tamper alert.",
    platforms: ['android'],
  },
  {
    text: 'On Android, {app} records detailed event timelines so you can see exactly how the device is being used between screenshots.',
    platforms: ['android', 'kindle'],
  },
  {
    text: 'On Android, if the operating system shuts {app} down, it automatically restarts itself within seconds.',
    platforms: ['android', 'kindle'],
  },
  {
    text: 'On Android, {app} does its best to avoid capturing screenshots of financial and healthcare apps.',
    platforms: ['android', 'kindle'],
  },

  // ---------- Chromebook ----------
  {
    text: 'On Chromebook, {app} detects if it has been disabled and triggers a tamper alert.',
    platforms: ['chromebook'],
  },
  {
    text: 'On Chromebook, revoking the screen-share permission triggers a tamper alert.',
    platforms: ['chromebook'],
  },
  {
    text: 'On Chromebook, using an unmonitored second screen triggers a tamper alert.',
    platforms: ['chromebook'],
  },

  // ---------- iOS / iPad ----------
  {
    text: 'On iOS, {app} alerts the moment the Screen Time permission is revoked.',
    platforms: ['ios'],
  },
  {
    text: "On iOS, setting up a Screen Time PIN prevents Screen Time — and {app}'s monitoring — from being disabled without the PIN.",
    platforms: ['ios'],
  },
  {
    text: 'Truple was the first accountability app to offer device-wide screenshots on iOS.',
    platforms: ['ios'],
  },
  {
    text: 'On iPhone, we capture screenshots of the entire device, including social media apps and more.',
    platforms: ['ios'],
  },
  {
    text: 'On iOS, a lock-screen widget makes unlocking monitored apps quick and convenient.',
    platforms: ['ios'],
  },
  {
    text: '{app} integrates with iOS Shortcuts automations so you can start monitoring with a tap or an automated routine.',
    platforms: ['ios'],
  },

  // ---------- Mac / Windows / Linux (shared desktop) ----------
  {
    text: 'On Mac, Windows, and Linux, {app} captures every monitor you have connected — nothing hides on a second screen.',
    platforms: ['mac', 'windows', 'linux'],
  },
  {
    text: 'On Mac, Windows, and Linux, if {app} is forcibly killed, it triggers a tamper alert.',
    platforms: ['mac', 'windows', 'linux'],
  },
  {
    text: 'On Mac, Windows, and Linux, {app} monitors activity across every user account on the device.',
    platforms: ['mac', 'windows', 'linux'],
  },

  // ---------- macOS ----------
  {
    text: 'On macOS, booting into safe mode triggers a tamper alert.',
    platforms: ['mac'],
  },
  {
    text: 'On macOS, using guest mode triggers a tamper alert.',
    platforms: ['mac'],
  },
  {
    text: "On macOS, {app} automatically restarts itself within seconds if it's killed or crashes.",
    platforms: ['mac'],
  },
  {
    text: 'On macOS, {app} watches its own processes and reports any that get killed.',
    platforms: ['mac'],
  },

  // ---------- Windows ----------
  {
    text: 'On Windows, booting into safe mode triggers a tamper alert.',
    platforms: ['windows'],
  },
  {
    text: 'On Windows, {app} notices when system logs are manipulated to hide tampering — and triggers an alert.',
    platforms: ['windows'],
  },
  {
    text: 'On Windows, {app} watches for apps that try to block screenshot capture and triggers a tamper alert.',
    platforms: ['windows'],
  },

  // ---------- Linux ----------
  {
    text: 'On Linux, {app} monitors auto-launch systems and reports any tampering.',
    platforms: ['linux'],
  },
]

export function getLoadingTips() {
  const app = getAppName()
  const product = getCurrentProduct()
  return LoadingTips
      .filter(t => !t.products || t.products.includes(product))
      .map(t => ({ ...t, text: t.text.replace(/\{app\}/g, app) }))
}

// ---------- Shared rotation singleton ----------
// Lets multiple LoadingTipsCard instances stay in sync across navigations
// (e.g. Login spinner -> HomeV2 spinner) so the visible tip doesn't jump.

let _shuffled = null
let _currentIndex = 0
let _timer = null
const _listeners = new Set()

function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
}

function ensureSharedInitialized() {
  if (_shuffled) return
  _shuffled = getLoadingTips()
  shuffleInPlace(_shuffled)
}

function rotateShared() {
  if (!_shuffled || _shuffled.length === 0) return
  _currentIndex = (_currentIndex + 1) % _shuffled.length
  _listeners.forEach(fn => fn(_currentIndex, _shuffled[_currentIndex]))
}

export function getCurrentSharedTip() {
  ensureSharedInitialized()
  return { index: _currentIndex, tip: _shuffled[_currentIndex] }
}

// Subscribe to shared rotation. Returns an unsubscribe function.
// The rotation timer is created lazily on first subscriber and cleared
// when the last subscriber unsubscribes — so the current index is preserved
// across unmount/remount, but the timer doesn't run when nothing is listening.
export function subscribeToSharedRotation(listener, rotateMs = 7000) {
  ensureSharedInitialized()
  _listeners.add(listener)
  if (!_timer && rotateMs > 0) {
    _timer = setInterval(rotateShared, rotateMs)
  }
  return () => {
    _listeners.delete(listener)
    if (_listeners.size === 0 && _timer) {
      clearInterval(_timer)
      _timer = null
    }
  }
}

export default LoadingTips
